import React, { useEffect, useMemo, useState } from 'react';
import { Users, Edit, Trash2, RefreshCw, Save, X, UserPlus, Search, Mail, UserRound } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import api from '../../services/api';
import authService from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';

const roleOptions = [
  { value: 'employe', label: 'Employé' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

const emptyForm = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  username: '',
  role_name: 'employe',
};

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const isAdmin = useMemo(() => authService.isAdmin(), []);
  const isManager = useMemo(() => authService.isManager(), []);
  const currentUser = useMemo(() => authService.getUserData(), []);

  const { t, language } = useLanguage();

  const getLocaleForLanguage = (lang) => {
    if (lang === 'en') return 'en-US';
    if (lang === 'pt') return 'pt-PT';
    return 'fr-FR';
  };

  const dateLocale = getLocaleForLanguage(language);

  const mapRoleKey = (roleName) => {
    const base = (roleName || '').toString().toLowerCase();
    if (base === 'employe') return 'employee';
    if (base === 'manager') return 'manager';
    if (base === 'admin') return 'admin';
    return 'employee';
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/auth/users/');
      const data = res?.data;
      setUsers((data && Array.isArray(data.results)) ? data.results : (data || []));
      setLastUpdated(new Date());
    } catch (e) {
      setError(t('users.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const onChange = (setter) => (e) => {
    const { name, value, type, checked } = e.target;
    setter((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const createUser = async () => {
    setCreating(true);
    setError('');
    
    const loadingToast = toast.loading(t('users.toasts.createLoading'), { position: 'top-right' });
    
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.username) delete payload.username;
      await api.post('/auth/users/', payload);
      setForm(emptyForm);
      setShowCreateModal(false);
      await loadUsers();
      
      toast.success(t('users.toasts.createSuccess'), { id: loadingToast, duration: 3000, position: 'top-right' });
    } catch (e) {
      setError(t('users.errors.create'));
      toast.error(
        <div>
          <div className="font-semibold">{t('users.toasts.createErrorTitle')}</div>
          <div className="text-sm mt-1">{t('users.toasts.createErrorText')}</div>
        </div>,
        { id: loadingToast, duration: 4000, position: 'top-right' }
      );
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({
      email: u.email || '',
      password: '',
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      username: u.username || '',
      role_name: u.role_name || 'employe',
    });
    setShowEditModal(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyForm);
  };

  const saveEdit = async (id) => {
    setLoading(true);
    setError('');
    
    const loadingToast = toast.loading(t('users.toasts.editLoading'), { position: 'top-right' });
    
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.patch(`/auth/users/${id}/`, payload);
      setEditingId(null);
      setShowEditModal(false);
      await loadUsers();
      
      toast.success(t('users.toasts.editSuccess'), { id: loadingToast, duration: 3000, position: 'top-right' });
    } catch (e) {
      setError(t('users.errors.edit'));
      toast.error(
        <div>
          <div className="font-semibold">{t('users.toasts.editErrorTitle')}</div>
          <div className="text-sm mt-1">{t('users.toasts.editErrorText')}</div>
        </div>,
        { id: loadingToast, duration: 4000, position: 'top-right' }
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    const confirmed = await new Promise((resolve) => {
      toast((toastObj) => (
        <div className="flex flex-col gap-3">
          <div>
            <div className="font-semibold text-gray-900 mb-1">{t('users.confirmDelete.title')}</div>
            <div className="text-sm text-gray-600">{t('users.confirmDelete.subtitle')}</div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(false); }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              {t('users.buttons.cancel')}
            </button>
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(true); }}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              {t('users.actions.delete')}
            </button>
          </div>
        </div>
      ), {
        duration: Infinity,
        position: 'top-center',
        style: { maxWidth: '500px', padding: '16px' },
      });
    });
    
    if (!confirmed) return;
    
    setLoading(true);
    setError('');
    
    const loadingToast = toast.loading(t('users.toasts.deleteLoading'), { position: 'top-right' });
    
    try {
      await api.delete(`/auth/users/${id}/`);
      await loadUsers();
      toast.success(t('users.toasts.deleteSuccess'), { id: loadingToast, duration: 3000, position: 'top-right' });
    } catch (e) {
      setError(t('users.errors.delete'));
      toast.error(t('users.toasts.deleteError'), { id: loadingToast, duration: 4000, position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter(u => {
      // Exclure l'utilisateur connecté de la liste
      if (currentUser && u.id === currentUser.id) return false;
      const matchesQ = !q || (
        (u.email || '').toLowerCase().includes(q) ||
        (u.first_name || '').toLowerCase().includes(q) ||
        (u.last_name || '').toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q)
      );
      const matchesRole = !roleFilter || u.role_name === roleFilter;
      return matchesQ && matchesRole;
    });
  }, [users, query, roleFilter, currentUser]);

  const initialsOf = (name, email) => {
    const base = (name && name.trim()) || email || '';
    const parts = base.split(' ').filter(Boolean);
    const raw = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
    return (raw || (email?.[0] || 'U')).toUpperCase();
  };

  const canAccess = isAdmin || isManager;
  const canWrite = isAdmin; // managers read-only

  if (!canAccess) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-800">{t('users.accessDeniedTitle')}</h1>
        <p className="text-gray-600">{t('users.accessDeniedText')}</p>
      </div>
    );
  }

  

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Toaster />
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Users size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{t('users.pageTitle')}</h1>
              <p className="text-sm text-gray-500">{t('users.pageSubtitle')}</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            {canWrite && (
              <button
                className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm shadow-sm"
                onClick={() => setShowCreateModal(true)}
              >
                <UserPlus size={16} /> {t('users.buttons.newUser')}
              </button>
            )}
            <button
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm"
              onClick={loadUsers}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> {t('users.buttons.refresh')}
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: '240px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white', padding: '6px 12px' }}>
            <Search size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
            <input
              style={{ flex: 1, marginLeft: '8px', backgroundColor: 'transparent', border: 'none', outline: 'none', fontSize: '14px' }}
              placeholder={t('users.search.placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div>
            <select
              className="w-full sm:w-48 px-3 py-2 border rounded-lg text-sm bg-white"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">{t('users.filter.allRoles')}</option>
              {roleOptions.map(o => {
                const key = mapRoleKey(o.value);
                return (
                  <option key={o.value} value={o.value}>{t(`users.roles.${key}`)}</option>
                );
              })}
            </select>
          </div>
          <div className="sm:hidden flex gap-2">
            {canWrite && (
              <button
                className="inline-flex flex-1 items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm shadow-sm"
                onClick={() => setShowCreateModal(true)}
              >
                <UserPlus size={16} /> {t('users.buttons.addUser')}
              </button>
            )}
            <button
              className="inline-flex flex-1 items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm"
              onClick={loadUsers}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {lastUpdated && (
          <div className="text-xs text-gray-500">{t('users.lastUpdated')}: {lastUpdated.toLocaleString(dateLocale)}</div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 border border-red-200 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      {/* Formulaire de création */}

      {/* Liste des utilisateurs - Grille responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map((u) => (
          <div key={u.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-lg flex-shrink-0">
                {initialsOf(`${u.first_name || ''} ${u.last_name || ''}`.trim(), u.email)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 font-medium flex items-center gap-1 truncate">
                  <UserRound size={14} className="text-gray-400 flex-shrink-0" /> 
                  <span className="truncate">{u.first_name} {u.last_name}</span>
                </div>
                <div className="text-gray-500 text-xs flex items-center gap-1 truncate">
                  <Mail size={12} className="text-gray-400 flex-shrink-0" /> 
                  <span className="truncate">{u.email}</span>
                </div>
                <div className="text-gray-400 text-xs truncate">@{u.username}</div>
              </div>
            </div>
            
            <div className="mt-3 flex items-center justify-between">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${((u.role_name || '').toString().toLowerCase() === 'admin')
                ? 'bg-primary/10 text-primary'
                : ((u.role_name || '').toString().toLowerCase() === 'manager')
                  ? 'bg-violet-100 text-violet-700'
                  : 'bg-amber-100 text-amber-700'}`}>{((u.role_name || '').toString().toUpperCase())}</span>
              
              {canWrite && (
                <div className="flex gap-2">
                  <button className="p-1.5 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors" onClick={() => startEdit(u)} title="Modifier">
                    <Edit size={16} />
                  </button>
                  <button className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" onClick={() => deleteUser(u.id)} title="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {filteredUsers.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">{t('users.list.empty')}</div>
        )}
      </div>

      {canWrite && showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateModal(false)}></div>
          <div className="relative bg-white w-full max-w-2xl rounded-lg shadow-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><UserPlus size={18} /> Ajouter un utilisateur</h3>
              <button className="p-1 rounded hover:bg-gray-100" onClick={() => setShowCreateModal(false)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.email')}</label>
                <input name="email" value={form.email} onChange={onChange(setForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.password')}</label>
                <input type="password" name="password" value={form.password} onChange={onChange(setForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.username')}</label>
                <input name="username" value={form.username} onChange={onChange(setForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.firstName')}</label>
                <input name="first_name" value={form.first_name} onChange={onChange(setForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.lastName')}</label>
                <input name="last_name" value={form.last_name} onChange={onChange(setForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.role')}</label>
                <select name="role_name" value={form.role_name} onChange={onChange(setForm)} className="w-full border rounded p-2 text-sm">
                  {roleOptions.map(o => {
                    const key = mapRoleKey(o.value);
                    return (
                      <option key={o.value} value={o.value}>{t(`users.roles.${key}`)}</option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="inline-flex items-center gap-2 px-3 py-2 rounded border bg-gray-100 hover:bg-gray-200" onClick={() => setShowCreateModal(false)}>
                <X size={16} /> {t('users.buttons.cancel')}
              </button>
              <button className="inline-flex items-center gap-2 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={createUser} disabled={creating}>
                <Save size={16} /> {t('users.buttons.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {canWrite && showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowEditModal(false); setEditingId(null); }}></div>
          <div className="relative bg-white w-full max-w-2xl rounded-lg shadow-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Edit size={18} /> {t('users.modals.editTitle')}</h3>
              <button className="p-1 rounded hover:bg-gray-100" onClick={() => { setShowEditModal(false); setEditingId(null); }}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <input name="email" value={editForm.email} onChange={onChange(setEditForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.newPasswordOptional')}</label>
                <input name="password" type="password" value={editForm.password} onChange={onChange(setEditForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Nom d'utilisateur</label>
                <input name="username" value={editForm.username} onChange={onChange(setEditForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Prénom</label>
                <input name="first_name" value={editForm.first_name} onChange={onChange(setEditForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Nom</label>
                <input name="last_name" value={editForm.last_name} onChange={onChange(setEditForm)} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('users.fields.role')}</label>
                <select name="role_name" value={editForm.role_name} onChange={onChange(setEditForm)} className="w-full border rounded p-2 text-sm">
                  {roleOptions.map(o => {
                    const key = mapRoleKey(o.value);
                    return (
                      <option key={o.value} value={o.value}>{t(`users.roles.${key}`)}</option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="inline-flex items-center gap-2 px-3 py-2 rounded border bg-gray-100 hover:bg-gray-200" onClick={() => { setShowEditModal(false); setEditingId(null); }}>
                <X size={16} /> {t('users.buttons.cancel')}
              </button>
              <button className="inline-flex items-center gap-2 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={() => saveEdit(editingId)} disabled={loading}>
                <Save size={16} /> {t('users.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
